import { useCallback, useEffect, useMemo } from 'react'
import debounce from 'lodash/debounce'
import { EntityLocationDTO } from '@user/store/map/typings'

type Props = {
  hereMap?: H.Map
  onViewChange?: (
    forceUpdate: boolean,
    topLeft?: H.geo.Point,
    bottomRight?: H.geo.Point,
    center?: H.geo.Point,
    zoom?: number,
  ) => void
  centerProp?: H.geo.IPoint
  zoomProp?: number
  animatePositionChange?: boolean
  useBrowserLocation?: boolean
  handleAddLeadSaveSuccess?: (newPin: EntityLocationDTO) => void
}

function storeMapStateToLocalStorage(center?: H.geo.Point, zoom?: number) {
  if (
    center &&
    typeof center.lat === 'number' &&
    typeof center.lng === 'number'
  ) {
    localStorage.setItem(
      'mapCenter',
      JSON.stringify({ lat: center.lat, lng: center.lng }),
    )
  }
  if (typeof zoom === 'number') {
    localStorage.setItem('mapZoom', zoom.toString())
  }
}

export function useMapEvents(props: Props) {
  const {
    hereMap,
    onViewChange,
    centerProp,
    zoomProp,
    animatePositionChange = true,
    handleAddLeadSaveSuccess,
  } = props

  const debounceDelay = Number(process.env.REACT_APP_MAP_DEBOUNCE_DELAY) || 500

  const resetMapState = useCallback(
    (forceUpdate: boolean) => {
      if (!hereMap) return
      const zoom = hereMap.getZoom()
      const bounds = hereMap.getViewModel().getLookAtData().bounds
      const topLeft = bounds?.getBoundingBox().getTopLeft()
      const bottomRight = bounds?.getBoundingBox().getBottomRight()
      const centerPoint = bounds?.getBoundingBox().getCenter()

      storeMapStateToLocalStorage(centerPoint, zoom)
      if (onViewChange) {
        onViewChange(forceUpdate, topLeft, bottomRight, centerPoint, zoom)
      }
    },
    [hereMap, onViewChange],
  )

  const debouncedOnViewChange = useMemo(() => {
    return debounce(() => {
      resetMapState(false)
    }, debounceDelay)
  }, [debounceDelay, resetMapState])

  const handleMapAfterAddLeadSaveSuccess = useCallback(
    (newPin: EntityLocationDTO) => {
      resetMapState(true)
      handleAddLeadSaveSuccess?.(newPin)
    },
    [handleAddLeadSaveSuccess, resetMapState],
  )

  useEffect(() => {
    if (!hereMap) return

    hereMap.addEventListener('mapviewchangeend', debouncedOnViewChange)
    window.addEventListener('resize', debouncedOnViewChange)
    debouncedOnViewChange()

    return () => {
      hereMap.removeEventListener('mapviewchangeend', debouncedOnViewChange)
      window.removeEventListener('resize', debouncedOnViewChange)
      debouncedOnViewChange.cancel()
    }
  }, [hereMap, debouncedOnViewChange])

  useEffect(() => {
    if (hereMap && centerProp) {
      hereMap.setCenter(centerProp, animatePositionChange)
    }
  }, [hereMap, centerProp, animatePositionChange])

  useEffect(() => {
    if (hereMap && zoomProp != null) {
      hereMap.setZoom(zoomProp, animatePositionChange)
    }
  }, [animatePositionChange, hereMap, zoomProp])

  const manualRefresh = useCallback(() => {
    resetMapState(true)
  }, [resetMapState])

  return {
    handleMapAfterAddLeadSaveSuccess,
    manualRefresh,
  }
}
